import React, { Component } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { readJson } from '../actions/apiResponse';

// The server's floor is Users.MinPasswordLength in Models/Utils/Users.cs.
// Keep the two equal, or the form will accept a password the server refuses.
export const MIN_PASSWORD_LENGTH = 8;

const PARAM = 'resetToken';

// The reset email links to "/?resetToken=...". Parsed by hand rather than
// with URLSearchParams, which the jsdom under this Jest does not have.
export function readResetToken(search) {
  if (typeof search !== 'string' || search.length < 2) {
    return null;
  }
  const pairs = search.replace(/^\?/, '').split('&');
  for (let i = 0; i < pairs.length; i++) {
    const eq = pairs[i].indexOf('=');
    const key = eq < 0 ? pairs[i] : pairs[i].slice(0, eq);
    if (key === PARAM) {
      const value = eq < 0 ? '' : decodeURIComponent(pairs[i].slice(eq + 1));
      return value.length > 0 ? value : null;
    }
  }
  return null;
}

// The query string with the token taken out and everything else left alone.
export function withoutResetToken(search) {
  if (typeof search !== 'string' || search.length < 2) {
    return '';
  }
  const kept = search.replace(/^\?/, '').split('&').filter(pair => {
    const eq = pair.indexOf('=');
    const key = eq < 0 ? pair : pair.slice(0, eq);
    return pair.length > 0 && key !== PARAM;
  });
  return kept.length > 0 ? '?' + kept.join('&') : '';
}

// replaceState, not a navigation: App.js wires beforeunload to a full logout,
// and a spent token left in the address bar would only show "invalid link"
// on the next refresh.
export function clearResetTokenFromUrl(win) {
  const w = win || window;
  if (!w || !w.history || typeof w.history.replaceState !== 'function' || !w.location) {
    return false;
  }
  const loc = w.location;
  w.history.replaceState(null, '', (loc.pathname || '/') + withoutResetToken(loc.search) + (loc.hash || ''));
  return true;
}

// Null when the pair is acceptable, otherwise what to tell the user.
export function validateNewPassword(newPassword, confirmPassword) {
  if (!newPassword || newPassword.length < MIN_PASSWORD_LENGTH) {
    return 'Use at least ' + MIN_PASSWORD_LENGTH + ' characters.';
  }
  if (newPassword !== confirmPassword) {
    return 'The passwords do not match.';
  }
  return null;
}

const postJson = (fetchFn, url, body) => fetchFn(url, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  body: JSON.stringify(body),
});

// Anonymous, so there is no token to guard on. The server answers the same
// 200 whether or not the address has an account, so "sent" here means only
// that the request was accepted - which is all the user can be told.
export function requestPasswordReset(email, fetchFn) {
  const f = fetchFn || ((...args) => fetch(...args));
  return postJson(f, 'api/auth/requestPasswordReset', { email })
    .then(resp => readJson(resp, 'api/auth/requestPasswordReset'))
    .then(() => true)
    .catch(err => {
      console.error('api/auth/requestPasswordReset failed:', err);
      return false;
    });
}

// Resolves { ok } or { ok: false, message }. Never signs anyone in: the
// server answers with no token, and the user logs in with the new password.
export function submitPasswordReset(token, newPassword, fetchFn) {
  const f = fetchFn || ((...args) => fetch(...args));
  return postJson(f, 'api/auth/resetPassword', { token, newPassword })
    .then(resp => Promise.resolve(readJson(resp, 'api/auth/resetPassword'))
      .then(data => {
        if (resp.ok) {
          return { ok: true };
        }
        return {
          ok: false,
          message: (data && data.message) || 'This reset link is invalid or has expired.',
        };
      }))
    .catch(err => {
      console.error('api/auth/resetPassword failed:', err);
      return { ok: false, message: 'TeamX could not be reached. Try again in a moment.' };
    });
}

// Capped, never floored: the main window is a narrow sidebar. See
// PreferencesDialog.
const paperStyle = { width: 'calc(100% - 10px)', maxWidth: '320px', margin: '5px' };

export class ResetPasswordForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      newPassword: '',
      confirmPassword: '',
      error: null,
      submitting: false,
    };
  }

  handleChange = event => {
    this.setState({ [event.target.name]: event.target.value, error: null });
  }

  handleKeyPress = event => {
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  }

  handleSubmit = event => {
    if (event !== undefined) {
      event.preventDefault();
    }
    if (this.state.submitting) {
      return Promise.resolve();
    }
    const error = validateNewPassword(this.state.newPassword, this.state.confirmPassword);
    if (error) {
      this.setState({ error });
      return Promise.resolve();
    }
    this.setState({ submitting: true, error: null });
    return submitPasswordReset(this.props.token, this.state.newPassword, this.props.fetchFn)
      .then(result => {
        if (result.ok) {
          this.props.onReset();
          return;
        }
        this.setState({ submitting: false, error: result.message });
      });
  }

  render() {
    return (
      <Dialog
        open
        aria-labelledby="reset-dialog-title"
        style={{ backgroundColor: '#adadad' }}
        PaperProps={{ style: paperStyle }}
      >
        <DialogTitle id="reset-dialog-title" style={{ backgroundColor: '#3f51b5' }}>
          <div style={{ color: 'white', textAlign: 'center', fontSize: '20px', cursor: 'default' }}>Set a new password</div>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            name="newPassword"
            label="New password"
            type="password"
            autoComplete="new-password"
            value={this.state.newPassword}
            onChange={this.handleChange}
            onKeyPress={this.handleKeyPress}
            helperText={'At least ' + MIN_PASSWORD_LENGTH + ' characters'}
          />
          <TextField
            margin="dense"
            fullWidth
            name="confirmPassword"
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={this.state.confirmPassword}
            onChange={this.handleChange}
            onKeyPress={this.handleKeyPress}
          />
          {this.state.error &&
            <p style={{ color: '#ff0000', fontSize: '11px', margin: '8px 0 0' }}>{this.state.error}</p>}
        </DialogContent>
        <DialogActions style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* A dead or spent link must not leave the user with no way back
              to the login form. */}
          <Button color="primary" onClick={this.props.onCancel} disabled={this.state.submitting}>
            Back to sign in
          </Button>
          <Button color="primary" onClick={this.handleSubmit} disabled={this.state.submitting}>
            Set password
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default ResetPasswordForm;



// WEBPACK FOOTER //
// ./src/components/ResetPasswordForm.js